/**
 * ReportsList — the Reports tab's list screen: the kind filter, what is ready to
 * file now (with the leaves-the-company confirm), what is already filed, and the
 * older ReportEntry rows.
 */

import type { FileableWindow, FiledReport } from "../reports/report-client";
import { KIND_LABEL, clientBlocked, humanReportFilename, leavesTheCompany, rowMeta, titleOf } from "../reports/report-labels";
import type { ReportEntry } from "../reports/report-types";
import { CLIENT_REPORT_COMING_SOON } from "../triage/client-report-gate";
import type { ReportFilter } from "./ReportsView";

/* #15: `client` joined the set. There are three report kinds and the filter row
   offered only two, so the one a super most wants to isolate — the thing that
   leaves the company — could not be isolated at all. */
const FILTERS: ReadonlyArray<{ id: ReportFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "daily", label: "Daily" },
  { id: "weekly", label: "Weekly" },
  { id: "client", label: "Client" },
];

export interface ReportsListProps {
  filter: ReportFilter;
  setFilter: (filter: ReportFilter) => void;
  projectLabel?: string;
  filedStatus: "loading" | "ready" | "error";
  fileableShown: FileableWindow[];
  fileableInFilter: FileableWindow[];
  filedShown: FiledReport[];
  filtered: ReportEntry[];
  confirmWindow: FileableWindow | null;
  setConfirmWindow: (w: FileableWindow | null) => void;
  openPreview: (w: FileableWindow) => Promise<void>;
  openFiledDetail: (report: FiledReport) => Promise<void>;
  openDetail: (entry: ReportEntry) => Promise<void>;
  onComingSoon?: (message: string) => void;
  onFileWindow?: (w: FileableWindow) => void;
}

export function ReportsList({
  filter,
  setFilter,
  projectLabel,
  filedStatus,
  fileableShown,
  fileableInFilter,
  filedShown,
  filtered,
  confirmWindow,
  setConfirmWindow,
  openPreview,
  openFiledDetail,
  openDetail,
  onComingSoon,
  onFileWindow,
}: ReportsListProps) {
  return (
    <>
      <div class="reports-header">
        <p class="reports-title">Reports</p>
        <p class="reports-subtitle">Auto-drafted from your captures{projectLabel ? ` · ${projectLabel}` : ""}</p>
      </div>
      <div class="log-filters" data-testid="reports-filters">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            class="log-filter-pill"
            data-testid={`reports-filter-${f.id}`}
            aria-pressed={filter === f.id}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>
      {filedStatus === "loading" && <p data-testid="reports-filed-loading">Checking filed reports…</p>}
      {filedStatus === "error" && (
        <p data-testid="reports-filed-error">
          Reports are temporarily unavailable. Your captures are safe; try again after reconnecting.
        </p>
      )}
      {/* #15: what can be filed now. Nothing generates reports on a schedule —
          the app action IS the trigger — so this list is a function of the
          date, and it replaced the hardcoded fixtures that made the tab show
          old fake reports and no row for today. */}
      {fileableShown.length > 0 && (
        <section data-testid="reports-fileable">
          <p class="save-chip-group-hint">Ready to file</p>
          <ul data-testid="reports-fileable-list">
            {fileableShown.map((w) => {
              const id = `${w.report_kind}-${w.window_key}`;
              return (
                <li key={id} data-testid={`report-fileable-${id}`}>
                  {/* Openable again, and this time it leads somewhere. It was
                      briefly made inert on the reasoning that an unfiled
                      window has no artifact to show — true of the PDF, wrong
                      about the CONTENTS, which are a projection away and are
                      the thing you want to check BEFORE filing. */}
                  <button
                    type="button"
                    class="report-row-open"
                    data-testid={`report-fileable-open-${id}`}
                    onClick={() => void openPreview(w)}
                  >
                    <span class="report-row-title">{KIND_LABEL[w.report_kind]} report</span>
                    <span class="report-row-meta">{w.label}</span>
                  </button>
                  <div class="report-row-actions">
                    {/* #19: always "Draft" now — a filed window has left this
                        list entirely, so the two-state pill had exactly one
                        reachable state and the other one was the duplicate
                        row this task removed. */}
                    <span
                      class="report-status-word"
                      data-state="draft"
                      data-testid={`report-fileable-status-${id}`}
                    >
                      Draft
                    </span>
                    <button
                      type="button"
                      class="report-file-action"
                      data-testid={`report-file-${id}`}
                      aria-disabled={clientBlocked(w.report_kind)}
                      onClick={() => {
                        // A1: inert for the client report until the gate flips.
                        if (clientBlocked(w.report_kind)) {
                          onComingSoon?.(CLIENT_REPORT_COMING_SOON);
                          return;
                        }
                        // A2: the confirm follows "leaves the company", so the
                        // weekly takes it too — for its own reason, not by
                        // being mislabelled client-facing.
                        if (leavesTheCompany(w.report_kind)) setConfirmWindow(w);
                        else onFileWindow?.(w);
                      }}
                    >
                      Approve and file
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
          {confirmWindow && (
            <div class="report-send-confirm" data-testid="report-file-confirm-panel">
              <p class="report-send-confirm-q">
                {confirmWindow.report_kind === "client"
                  ? "File this as the CLIENT report?"
                  : "This weekly goes to the subs and designers on this job, and names who owns each open item. File it?"}
              </p>
              <button
                type="button"
                data-testid="report-file-confirm"
                onClick={() => {
                  onFileWindow?.(confirmWindow);
                  setConfirmWindow(null);
                }}
              >
                Yes, file it
              </button>
              <button
                type="button"
                data-testid="report-file-cancel"
                onClick={() => setConfirmWindow(null)}
              >
                Cancel
              </button>
            </div>
          )}
        </section>
      )}

      {/* #19: with filed windows out of "Ready to file", something has to say
          that an empty to-do section is a FINISHED state rather than a
          surface that failed to load. Quiet on purpose — "nothing to do"
          should not shout. */}
      {fileableShown.length === 0 && fileableInFilter.length > 0 && (
        <p class="reports-subtitle" data-testid="reports-nothing-to-file">
          Nothing left to file right now.
        </p>
      )}

      {/* #15: what is already in the folder Mercer opens. This is the question
          the tab exists to answer — "did I already do Tuesday's?" — and it had
          no answer at all while the list was fixtures. */}
      {filedShown.length > 0 && (
        <section data-testid="reports-filed">
          <p class="save-chip-group-hint">Filed</p>
          <ul data-testid="reports-filed-list">
            {filedShown.map((f) => (
              <li key={`${f.bucket}/${f.filename}`} data-testid={`report-filed-${f.filename}`}>
                {/* #19: openable now — this row owns re-filing, so the whole
                    text block is the tap target rather than a second button
                    competing with "Approve and file" one section up. */}
                <button
                  type="button"
                  class="report-row-open"
                  data-testid={`report-filed-open-${f.filename}`}
                  onClick={() => void openFiledDetail(f)}
                >
                  <span class="report-row-title">{humanReportFilename(f)}</span>
                  <span class="report-row-meta">
                    {f.bucket}{f.revision ? ` · Updated · Revision ${f.revision_number ?? 1}` : " · Original"}
                  </span>
                </button>
                <span class="report-filed-actions">
                  <span class="report-status-word" data-state="filed">Filed</span>
                  <span class="report-item-chevron" aria-hidden="true">›</span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <ul data-testid="reports-view-list">
        {filtered.map((r) => (
          <li key={r.pdf_key} data-testid={`report-row-${r.pdf_key}`}>
            <button type="button" class="report-row-open" onClick={() => void openDetail(r)}>
              <span class="report-row-title">{titleOf(r)}</span>
              <span class="report-row-meta">{rowMeta(r)}</span>
            </button>
            {/* Read-model status; Stage A lists everything as draft (sent
                reflection is read-gated, S8). Spec pillTone: draft → "high"
                (amber outline + amber text), sent → "none" (muted). */}
            <span class="criticality-pill" data-criticality="high" data-testid={`report-row-status-${r.pdf_key}`}>
              draft
            </span>
          </li>
        ))}
      </ul>
      {filedStatus === "ready" && filtered.length === 0 && filedShown.length === 0 && fileableShown.length === 0 && (
        <p data-testid="reports-view-empty">
          {filter === "all" ? "No reports yet." : `No ${filter} reports.`}
        </p>
      )}
    </>
  );
}
